// PortfolioTracker.js

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CoinDetails from './CoinDetails';

const holdingsData = [
      { id: 'bitcoin', symbol: 'BTC', amount: 0.5, cost_basis: 40000 }, // Replace with your actual data
      { id: 'ethereum', symbol: 'ETH', amount: 2, cost_basis: 2000 },    // Replace with your actual data
      // Add more coins here...
];

function PortfolioTracker() {
      const [coins, setCoins] = useState([]);
      const [selectedCoin, setSelectedCoin] = useState(null);
      const [history, setHistory] = useState([]);
      const [loading, setLoading] = useState(true);
      const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // Default to 30 days ago
      const [endDate, setEndDate] = useState(new Date());
      const holdings = useMemo(() => holdingsData, []);
      // Theme-aligned palette: electric-blue, electric-purple, electric-pink, electric-green
      const COLORS = ['#00D4FF', '#7C3AED', '#EC4899', '#10B981'];

      useEffect(() => {
            fetchCoinData();
      }, []);

      const fetchCoinData = async () => {
            try {
                  const response = await axios.get(`https://api.coingecko.com/api/v3/coins/markets`, {
                        params: {
                              vs_currency: 'usd',
                              ids: holdings.map((holding) => holding.id).join(','),
                        },
                  });
                  const updatedHoldings = holdings.map((holding) => {
                        const coin = response.data.find((coin) => coin.id === holding.id);
                        return {
                              ...holding,
                              name: coin.name,
                              current_price: coin.current_price,
                              price_change_24h: coin.price_change_24h,
                              price_change_percentage_24h: coin.price_change_percentage_24h,
                        };
                  });
                  setCoins(updatedHoldings);
                  setLoading(false);
            } catch (error) {
                  console.error('Error fetching data', error);
                  setLoading(false);
            }
      };

      const handleCoinSelect = (id) => {
            const selected = coins.find((coin) => coin.id === id);
            setSelectedCoin(selected);
            fetchHistoricalData(id);
      };

      const fetchHistoricalData = async (id) => {
            setLoading(true);
            try {
                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                  const response = await axios.get(`https://api.coingecko.com/api/v3/coins/${id}/market_chart`, {
                        params: {
                              vs_currency: 'usd',
                              days: days,
                        },
                  });
                  setHistory(response.data.prices);
                  setLoading(false);
            } catch (error) {
                  console.error('Error fetching historical data', error);
                  setLoading(false);
            }
      };

      // Calculate total portfolio value
      const totalValue = coins.reduce((total, coin) => total + coin.current_price * coin.amount, 0);

      // Calculate total invested amount
      const totalInvested = coins.reduce((total, coin) => total + coin.cost_basis * coin.amount, 0);

      // Calculate ROI and P&L
      const roi = ((totalValue - totalInvested) / totalInvested) * 100;
      const pnl = totalValue - totalInvested;

      return (
            <div
                  style={{
                        border: '1px solid rgba(255,255,255,0.1)',
                        margin: '10px',
                        padding: '20px',
                        borderRadius: '12px',
                        boxSizing: 'border-box',
                        background: 'rgba(255,255,255,0.03)',
                        color: 'var(--text-primary)',
                  }}
            >
                  <h2>Portfolio Tracker</h2>
                  {loading ? (
                        <p>Loading...</p>
                  ) : (
                        <>
                              <ResponsiveContainer width="100%" height={400}>
                                    <PieChart>
                                          <Pie
                                                data={coins}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="amount"
                                                nameKey="name"
                                          >
                                                {coins.map((entry, index) => (
                                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                          </Pie>
                                          <Tooltip />
                                    </PieChart>
                              </ResponsiveContainer>
                              <h3 style={{ color: 'var(--text-primary)', margin: '1rem 0 0.5rem' }}>Holdings</h3>
                              <table className="portfolio-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                          <tr style={{ background: 'rgba(0,212,255,0.07)', borderBottom: '2px solid rgba(255,255,255,0.1)' }}>
                                                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coin</th>
                                                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                                                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Value</th>
                                                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>24h Change ($)</th>
                                                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>24h Change (%)</th>
                                          </tr>
                                    </thead>
                                    <tbody>
                                          {coins.map((coin, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                      <td style={{ padding: '0.75rem 1rem', color: 'var(--text-primary)', fontWeight: 600 }}>{coin.name}</td>
                                                      <td style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)' }}>{coin.amount}</td>
                                                      <td style={{ padding: '0.75rem 1rem', color: 'var(--text-primary)' }}>${(coin.current_price * coin.amount).toFixed(2)}</td>
                                                      <td style={{ padding: '0.75rem 1rem', color: coin.price_change_24h >= 0 ? 'var(--success)' : 'var(--danger)' }}>${(coin.price_change_24h * coin.amount).toFixed(2)}</td>
                                                      <td style={{ padding: '0.75rem 1rem', color: coin.price_change_percentage_24h >= 0 ? 'var(--success)' : 'var(--danger)' }}>{coin.price_change_percentage_24h?.toFixed(2)}%</td>
                                                </tr>
                                          ))}
                                    </tbody>
                              </table>
                              <div style={{ display: 'flex', gap: '1.5rem', margin: '1rem 0', flexWrap: 'wrap' }}>
                                    <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '0.75rem 1.25rem' }}>
                                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Value</div>
                                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>${totalValue.toFixed(2)}</div>
                                    </div>
                                    <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '0.75rem 1.25rem' }}>
                                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ROI</div>
                                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: roi >= 0 ? 'var(--success)' : 'var(--danger)' }}>{roi.toFixed(2)}%</div>
                                    </div>
                                    <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '0.75rem 1.25rem' }}>
                                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>P&L</div>
                                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: pnl >= 0 ? 'var(--success)' : 'var(--danger)' }}>${pnl.toFixed(2)}</div>
                                    </div>
                              </div>
                              <h2 style={{ color: 'var(--text-primary)', margin: '1rem 0 0.75rem' }}>Trends</h2>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <select
                                          className="form-select form-select-sm"
                                          value={selectedCoin ? selectedCoin.id : ''}
                                          onChange={(e) => handleCoinSelect(e.target.value)}
                                          style={{ maxWidth: '160px' }}
                                    >
                                          <option value="">Select Coin</option>
                                          {coins.map((coin) => (
                                                <option key={coin.id} value={coin.id}>
                                                      {coin.name} ({coin.symbol.toUpperCase()})
                                                </option>
                                          ))}
                                    </select>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Start:</span>
                                          <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="form-control form-control-sm" />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>End:</span>
                                          <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="form-control form-control-sm" />
                                    </div>
                                    <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => selectedCoin && fetchHistoricalData(selectedCoin.id)}
                                    >
                                          Update Chart
                                    </button>
                              </div>
                              {selectedCoin && <CoinDetails coin={selectedCoin} history={history} />}
                        </>
                  )}
            </div>
      );
}

export default PortfolioTracker;


